import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AleloButton, AleloLayout } from '@/components/AleloLayout'
import styles from '@/components/AleloLayout.module.css'
import { RangoAlert } from '@/components/rango'
import { executeSafely } from '@/utils/helpers'
import * as signupFlowMarker from '@/utils/signupFlowMarker'
import locales from './locales/pt-BR.json'
import passkeyStyles from './passkey.module.css'

type PasskeySdk = {
  continuePasskeyEnrollment: () => Promise<void>
  abortPasskeyEnrollment: (payload?: { doNotShowAgain?: boolean }) => Promise<void>
}

export type PasskeyVariant = 'passkey-enrollment' | 'passkey-enrollment-local'

async function createSdk(variant: PasskeyVariant): Promise<PasskeySdk | null> {
  try {
    if (variant === 'passkey-enrollment-local') {
      const mod = await import('@auth0/auth0-acul-js/passkey-enrollment-local')
      return new mod.default() as unknown as PasskeySdk
    }
    const mod = await import('@auth0/auth0-acul-js/passkey-enrollment')
    return new mod.default() as unknown as PasskeySdk
  } catch {
    return null
  }
}

type ErrorKind = 'failed' | 'cancelled' | 'unsupported'

/** Classifica o erro do WebAuthn em mensagem clara (sem jargão). */
export function classifyPasskeyError(error: unknown): ErrorKind {
  const info = error as { name?: string; message?: string } | null
  const name = info?.name ?? ''
  const message = info?.message ?? String(error)
  if (name === 'NotSupportedError' || /not supported|not allowed/i.test(message)) {
    return 'unsupported'
  }
  if (
    name === 'NotAllowedError' ||
    name === 'AbortError' ||
    /timed out|cancel/i.test(message)
  ) {
    return 'cancelled'
  }
  return 'failed'
}

export function supportsWebAuthn(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window
}

/**
 * S14 — Enrollment (e login) com passkey.
 * Textos em `locales/pt-BR.json` (não hardcoded no TSX).
 */
export function PasskeyEnrollmentScreen({
  variant = 'passkey-enrollment',
}: {
  variant?: PasskeyVariant
}) {
  const [sdk, setSdk] = useState<PasskeySdk | null>(null)
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  const [done, setDone] = useState(false)

  const isLogin = variant === 'passkey-enrollment-local'
  const copy = isLogin ? locales.login : locales.enrollment
  // A Auth0 oferece a chave no cadastro, antes da senha, e a connection não
  // tem como desligar só ali. No cadastro a tela pula a oferta; a inscrição
  // progressiva depois do login segue normal (SMP-13).
  const [skipInSignup] = useState(
    () => variant === 'passkey-enrollment' && signupFlowMarker.isActive(),
  )
  const skipped = useRef(false)

  useEffect(() => {
    if (!skipInSignup || skipped.current) return
    skipped.current = true
    void (async () => {
      const instance = await createSdk(variant)
      await instance?.abortPasskeyEnrollment({ doNotShowAgain: false })
    })()
  }, [skipInSignup, variant])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSdk(variant)
      if (!cancelled) setSdk(instance)
    })()
    return () => {
      cancelled = true
    }
  }, [variant])

  async function onContinue(event?: FormEvent) {
    event?.preventDefault()
    if (pending) return
    setError(undefined)
    if (!supportsWebAuthn()) {
      setError(locales.unsupported)
      return
    }
    setPending(true)
    try {
      await executeSafely(async () => {
        const instance = sdk ?? (await createSdk(variant))
        if (instance?.continuePasskeyEnrollment) {
          await instance.continuePasskeyEnrollment()
          return
        }
        console.info(`[preview ${variant}] continue`)
        window.dispatchEvent(
          new CustomEvent('alelo:acul-preview', {
            detail: { screen: variant, action: 'continue' },
          }),
        )
      })
      if (!isLogin) setDone(true)
    } catch (err) {
      setError(locales[classifyPasskeyError(err)])
    } finally {
      setPending(false)
    }
  }

  async function onSkip() {
    if (pending) return
    setError(undefined)
    setPending(true)
    try {
      await executeSafely(async () => {
        const instance = sdk ?? (await createSdk(variant))
        if (instance?.abortPasskeyEnrollment) {
          await instance.abortPasskeyEnrollment({ doNotShowAgain: false })
          return
        }
        console.info(`[preview ${variant}] abort`)
        window.dispatchEvent(
          new CustomEvent('alelo:acul-preview', {
            detail: { screen: variant, action: 'abort' },
          }),
        )
      })
    } catch (err) {
      setError(locales[classifyPasskeyError(err)])
    } finally {
      setPending(false)
    }
  }

  if (skipInSignup) {
    return (
      <AleloLayout>
        <p className={styles.subtitle} role="status">
          {locales.skippingInSignup}
        </p>
      </AleloLayout>
    )
  }

  if (done) {
    return (
      <AleloLayout>
        <h1 className={styles.title}>{locales.successTitle}</h1>
        <RangoAlert tone="positive" title={locales.successTitle}>
          {locales.success}
        </RangoAlert>
      </AleloLayout>
    )
  }

  return (
    <AleloLayout>
      <div className={passkeyStyles.icon} aria-hidden="true">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="8" cy="15" r="4" />
          <path d="M10.8 12.2 20 3m-4 4 3 3m-6-1 2 2" />
        </svg>
      </div>
      {/* Sempre a copy Alelo: o texto padrão da Auth0 traz o nome da aplicação (SMP-14). */}
      <h1 className={styles.title}>{copy.title}</h1>
      <p className={styles.subtitle}>{copy.description}</p>
      <form className={styles.stack} onSubmit={(e) => void onContinue(e)}>
        {error ? <RangoAlert tone="negative">{error}</RangoAlert> : null}
        <div className={styles.actions}>
          <AleloButton type="submit" disabled={pending} loading={pending}>
            {copy.continue}
          </AleloButton>
          <AleloButton
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => void onSkip()}
          >
            {copy.skip}
          </AleloButton>
        </div>
        {!isLogin ? (
          <p className={`${styles.muted} ${passkeyStyles.note}`}>
            {locales.enrollment.notNowHint}
          </p>
        ) : null}
      </form>
    </AleloLayout>
  )
}

export default function PasskeyEnrollmentDefault() {
  return <PasskeyEnrollmentScreen variant="passkey-enrollment" />
}
